
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
}

export default function QuoteModal({ isOpen, onClose, productId, productName }: QuoteModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Récupérer les informations du client depuis la table client_users
      const { data: clientData, error: clientError } = await supabase
        .from('client_users')
        .select('email, full_name, phone, organization')
        .eq('id', user.id)
        .single();

      if (clientError) {
        console.error('Erreur lors de la récupération des données client:', clientError);
        throw new Error('Impossible de récupérer vos informations. Veuillez réessayer.');
      }

      if (!clientData) {
        throw new Error('Aucune information client trouvée. Veuillez contacter l\'administrateur.');
      }

      const { error: insertError } = await supabase
        .from('quote_requests')
        .insert([
          {
            user_id: user.id,
            user_email: clientData.email,
            user_name: clientData.full_name,
            user_phone: clientData.phone,
            user_organization: clientData.organization || '',
            product_id: productId,
            product_name: productName,
            quantity,
            status: 'pending',
          },
        ]);

      if (insertError) {
        console.error('Erreur lors de l\'insertion de la demande:', insertError);
        throw insertError;
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setQuantity(1);
      }, 2000);
    } catch (err: any) {
      console.error('Erreur complète:', err);
      setError(err.message || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">
              Demande envoyée !
            </h3>
            <p className="text-gray-600">
              Nous vous contacterons bientôt avec votre devis personnalisé.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-[#1A1A1A] mb-2">
              Demander un devis
            </h2>
            <p className="text-gray-600 mb-6">
              {productName}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantité souhaitée *
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-xl"></i>
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-center text-lg font-semibold focus:ring-2 focus:ring-[#E31B23] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-xl"></i>
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  <i className="ri-information-line mr-2"></i>
                  Notre équipe vous contactera dans les 24h pour vous fournir un devis détaillé et personnalisé.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E31B23] text-white py-3 rounded-lg font-medium hover:bg-[#C41820] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer la demande'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
